import * as members from '../../privates/privateMembers';
import {setAsyncFunctions} from '../../privates/utils/asyncDecorator';

/**
 * @mixin DisabledMixin
 * @summary Provides functionality for elements that can be disabled.
 * @description
 *  This mixin provides functions for disabling and enabling elements. When an element is enabled, 
 *  its `enabled` property is set to `true`.  When an element is disabled, 
 *  its `enabled` property is set to `false`.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  $w("#DisabledMixin").disable();
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that can be disabled and  
 *  "mixes in" `DisabledMixin`. 
 * 
 *   ```javascript
 *  $w("#myElement").disable();
 *  ```
 *  
 * @memberof $w
 */
class DisabledMixin {

    /**
     * @function disable
     * @syntax
     * function disable(): Promise<void>
     * @summary Disables the element and sets its `enabled` property to `false`.
     * @description
     *  The `disable()` function returns a Promise that is resolved when the
     *  element's [`enabled`](#enabled) property has been set to `false`.
     *
     *  To learn about the behavior of a disabled element,
     *  see the [`enabled`](#enabled) property.
     * @returns {Promise}
     * @fulfill {void} When the element's `enabled` property has been set to `false`.
     * @snippet [DisabledMixin-disable.es6=Disable an element]
     * @snippet [DisabledMixin-disable_then.es6=Disable an element and log a message when done]
     * @snippet [DisabledMixin_toggle_enabled.es6=Toggle an element's enabled state]
     * @see [enable( )](#enable)
     * @see [enabled](#enabled)
     * @memberof $w.DisabledMixin
     */
    disable() {
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);

            RMI.setProps(compId, {
                isDisabled: true
            }, resolve);
        });
    }

    /**
     * @function enable
     * @syntax
     * function enable(): Promise<void>
     * @summary Enables the element and sets its `enabled` property to `true`.
     * @description
     *  The `enable()` function returns a Promise that is resolved when the
     *  element's [`enabled`](#enabled) property has been set to `true`.
     *
     *  To learn about the behavior of an enabled element,
     *  see the [`enabled`](#enabled) property.
     * @returns {Promise}
     * @fulfill {void} When the element's `enabled` property has been set to `true`.
     * @snippet [DisabledMixin-enable.es6=Enable an element]
     * @snippet [DisabledMixin-enable_then.es6=Enable an element and log a message when done]
     * @snippet [DisabledMixin_toggle_enabled.es6=Toggle an element's enabled state]
     * @see [disable( )](#disable)
     * @see [enabled](#enabled)
     * @memberof $w.DisabledMixin
     */
    enable() {
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);

            RMI.setProps(compId, {
                isDisabled: false
            }, resolve);
        });
    }

    /**
     * @member enabled
     * @label property
     * @syntax
     * get enabled(): boolean
     * @summary Indicates if the element is enabled or disabled.
     * @description
     *  If `enabled` is `true`, users can interact with the element.
     *
     *  If `enabled` is `false`, users cannot interact with the element.
     *
     *  When an element is disabled:
     *
     *  + Its color is faded or grayed out.
     *  + Animations that the element normally exhibits when being interacting
     *    with do not occur.
     *  + Actions that the element has been configured to perform, such as
     *    opening a link, do not occur.
     *  + Event handlers that have been bound to the element, such as with
     *    [`onMouseIn`]($w.Element.onMouseIn), do not run.
     *  + If the element is an input element, such as a dropdown or text box,
     *    users cannot interact with it.
     *
     *
     *  To set the `enabled` property of an element, use the element's
     *  [`enable()`](#enable) or [`disable()`](#disable) functions.
     *
     *  The `enabled` property can also be set in the Editor using the Properties and Events panel.
     * @snippet [DisabledMixin-enabled.es6=Get an element's enabled status]
     * @snippet [DisabledMixin_toggle_enabled.es6=Toggle an element's enabled state]
     * @type {external:Boolean}
     * @see [disable( )](#disable)
     * @see [enable( )](#enable)
     * @memberof $w.DisabledMixin
     * @attr {external:Boolean} enabled
     * @readonly
     */
    get enabled() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const props = RMI.getProps(compId);
        return !props.isDisabled || props.isDisabled === false;
    }
}

setAsyncFunctions(['disable', 'enable']);

export default DisabledMixin;
